import { useEffect, useState } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend } from "recharts";
import { api } from "../lib/api";
import { storeName } from "../lib/labels";

// Consistent per-store colours across the dashboard (drawn from the trend
// chart palette so Roy's eye reads them the same way everywhere).
const STORE_COLORS = {
  "Public Butter Kensington": "#c72c2c",
  "Black Market 347": "#0a0a0a",
  "Black Market 256": "#c48a2b",
  "Dirty Mike's": "#3a7a5c",
};

function money(v) {
  const n = Number(v || 0);
  if (Math.abs(n) >= 1000) return `$${(n / 1000).toFixed(1)}k`;
  return `$${n.toFixed(0)}`;
}

function fmtWeekLabel(bucket) {
  // "2026-W23" → "W23 '26"
  if (typeof bucket !== "string") return bucket;
  const m = bucket.match(/^(\d{4})-W(\d{2})$/);
  if (!m) return bucket;
  return `W${m[2]} '${m[1].slice(2)}`;
}

/**
 * Weekly per-store trend for a single category. Includes a dotted "last year"
 * ghost line so Roy can see if this week is up/down vs the same week last
 * year. Toggle switch flips revenue ↔ units.
 */
export default function SubCategoryTrend({ category, range, outletIds, compareMode = "same_dates" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [metric, setMetric] = useState("revenue"); // "revenue" | "units"

  useEffect(() => {
    if (!category || !range) return;
    let cancel = false;
    setLoading(true);
    const params = {
      category,
      start_date: range.start,
      end_date: range.end,
      yoy_mode: compareMode === "same_period" ? "same_period" : "same_dates",
    };
    if (outletIds?.length) params.outlet_ids = outletIds.join(",");
    api.get("/products/subcategory-trend", { params })
      .then((r) => { if (!cancel) setData(r.data); })
      .catch(() => {})
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [category, range?.start, range?.end, outletIds?.join(","), compareMode]);

  const outlets = data?.outlets || [];
  const buckets = (metric === "units" ? data?.unit_buckets : data?.buckets) || [];
  const hasData = buckets.some((b) =>
    outlets.some((o) => Number(b[o] || 0) > 0)
  );
  const hasYoY = buckets.some((b) =>
    outlets.some((o) => Number(b[`${o}_yoy`] || 0) > 0)
  );

  return (
    <div className="pl-10 pr-4 py-3 border-b border-[#0a0a0a]/10" data-testid={`acc-cat-trend-${category}`}>
      <div className="flex items-baseline justify-between gap-3 mb-2 flex-wrap">
        <div className="bm-eyebrow text-neutral-500">
          Weekly trend · per store · {data?.matched_products ?? "…"} SKUs
          {hasYoY && (
            <span className="ml-2 text-neutral-400">
              · dashed lines = same store, last year ({data.yoy_start} → {data.yoy_end})
            </span>
          )}
        </div>
        <div className="inline-flex border border-[#0a0a0a]/30" data-testid="acc-cat-trend-metric">
          <button
            type="button"
            onClick={() => setMetric("revenue")}
            className={`bm-eyebrow px-2 py-1 ${metric === "revenue" ? "bg-[#0a0a0a] text-[#f5f2eb]" : "hover:bg-[#0a0a0a]/5"}`}
            data-testid="acc-cat-trend-revenue"
          >
            Revenue
          </button>
          <button
            type="button"
            onClick={() => setMetric("units")}
            className={`bm-eyebrow px-2 py-1 border-l border-[#0a0a0a]/30 ${metric === "units" ? "bg-[#0a0a0a] text-[#f5f2eb]" : "hover:bg-[#0a0a0a]/5"}`}
            data-testid="acc-cat-trend-units"
          >
            Units
          </button>
        </div>
      </div>

      {loading && <div className="text-sm text-neutral-500 py-6">Loading…</div>}

      {!loading && !hasData && !hasYoY && (
        <div className="bm-eyebrow text-neutral-500 py-6">No sales in this window.</div>
      )}

      {!loading && (hasData || hasYoY) && (
        <div className="h-52 w-full">
          <ResponsiveContainer>
            <LineChart data={buckets} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#0a0a0a15" />
              <XAxis
                dataKey="bucket"
                tickFormatter={fmtWeekLabel}
                tick={{ fontSize: 10, fill: "#525252", fontFamily: "monospace" }}
                stroke="#0a0a0a40"
              />
              <YAxis
                tickFormatter={metric === "revenue" ? money : (v) => v.toLocaleString()}
                tick={{ fontSize: 10, fill: "#525252", fontFamily: "monospace" }}
                stroke="#0a0a0a40"
                width={50}
              />
              <Tooltip
                labelFormatter={fmtWeekLabel}
                formatter={(v, name) => {
                  if (name.endsWith("_yoy")) {
                    return [metric === "revenue" ? money(v) : Number(v).toLocaleString(), `${storeName(name.replace(/_yoy$/, ""))} · LY`];
                  }
                  const label = name === "yoy_total" ? "Last year (all stores)" : storeName(name);
                  return [metric === "revenue" ? money(v) : Number(v).toLocaleString(), label];
                }}
                contentStyle={{ background: "#ede8dc", border: "1px solid #0a0a0a", fontSize: 12 }}
              />
              <Legend
                wrapperStyle={{ fontSize: 11, paddingTop: 4 }}
                formatter={(name) => {
                  if (name.endsWith("_yoy")) return `${storeName(name.replace(/_yoy$/, ""))} · LY`;
                  if (name === "yoy_total") return "Last year (all stores)";
                  return storeName(name);
                }}
              />
              {outlets.map((o) => (
                <Line
                  key={o}
                  type="monotone"
                  dataKey={o}
                  stroke={STORE_COLORS[o] || "#525252"}
                  strokeWidth={2}
                  dot={{ r: 2.5, strokeWidth: 0, fill: STORE_COLORS[o] || "#525252" }}
                  activeDot={{ r: 4 }}
                />
              ))}
              {hasYoY && outlets.map((o) => (
                <Line
                  key={`${o}_yoy`}
                  type="monotone"
                  dataKey={`${o}_yoy`}
                  stroke={STORE_COLORS[o] || "#525252"}
                  strokeWidth={1.4}
                  strokeDasharray="4 4"
                  strokeOpacity={0.4}
                  dot={false}
                  activeDot={{ r: 3 }}
                  legendType="plainline"
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
